import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
// @mui
import Collapse from '@mui/material/Collapse';
// routes
import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';
//
// import PropTypes from 'prop-types';
import { NavListProps, NavConfigProps } from '../types';
import NavItem from './nav-item';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
  config: NavConfigProps;
  open: string | null;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<string | null>>;
};

export default function NavList({
  data,
  depth,
  hasChild,
  config,
  open,
  onClose,
  setOpen,
}: NavListRootProps) {
  const pathname = usePathname();

  const active = useActiveLink(data.path, hasChild);

  const externalLink = data.path.includes('http');

  const isOpen = open === data.path;

  useEffect(() => {
    // Close the current item when pathname changes
    if (!active && isOpen) {
      if (onClose) onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, active]);

  useEffect(() => {
    // Auto-open only on route change; don't override manual expand/collapse interactions.
    if (hasChild && active && open !== data.path && setOpen) {
      setOpen(data.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      if (onClose) onClose();
    } else {
      // Open the clicked item and close others
      if (onClose) onClose();
      if (setOpen) setOpen(data.path);
    }
  }, [data.path, isOpen, onClose, setOpen]);
  // console.log(" all data ", isOpen, data, depth, open, active, externalLink, config)
  return (
    <>
      <NavItem
        item={data}
        depth={depth}
        open={isOpen}
        active={active}
        externalLink={externalLink}
        onClick={handleToggle}
        config={config}
      />

      {hasChild && (
        <Collapse in={isOpen} unmountOnExit>
          <NavSubList
            data={data.children}
            depth={depth}
            config={config}
            open={open}
            onClose={onClose}
            setOpen={setOpen}
          />
        </Collapse>
      )}
    </>
  );
}

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
  config: NavConfigProps;
  open: string | null;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<string | null>>;
};

function NavSubList({ data, depth, config, open, onClose, setOpen }: NavListSubProps) {
  // Inside the parent component
  const [openItem, setOpenItem] = useState<string | null>(null);

  const handleCloseItem = () => {
    setOpenItem(null);
  };
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          config={config}
          open={openItem}
          onClose={handleCloseItem}
          setOpen={setOpenItem}
        />
      ))}
    </>
  );
}
